// Billing and Payment API module
import { BaseApiClient } from './BaseApiClient';
import type {
  ApiResponse,
} from '@/types/api';
import type {
  PaymentAnalytics,
  PaymentAnalyticsSummary,
  BillingSnapshot,
  BillingSyncPayload,
  BillingSyncResponse,
  TierRecalculationResponse,
  TierOverridePayload,
  TierOverrideResponse,
  RefundRequest,
  RefundResponse,
  BillingDriftEntry,
  PendingCancellationsResponse,
  ProviderCustomerProfile,
  PaymentProvider,
  ProviderPaymentMetrics,
  TimeSeriesPaymentData,
  CurrencyAmount,
  UserCreditsResponse,
  IssueCreditRequest,
  IssueCreditResponse,
  ConsumeCreditRequest,
  ConsumeCreditResponse,
  RefreshBalanceResponse,
  OutstandingLiability,
  CancelSubscriptionWithCreditRequest,
  CancelSubscriptionWithCreditResponse,
  CreditCurrency,
} from '@/types/payment';
import type { DateRange } from '@/types/analytics';

type CurrencyAmountLike = Partial<CurrencyAmount> | null | undefined;

interface ProviderPaymentMetricsLike {
  active_subscriptions?: number;
  monthly_recurring_revenue?: CurrencyAmountLike;
  annual_recurring_revenue?: CurrencyAmountLike;
  churn_rate?: number;
  payment_success_rate?: number;
  failed_payments?: number;
}

interface PaymentAnalyticsSummaryLike {
  total_revenue?: unknown;
  monthly_recurring_revenue?: unknown;
  annual_recurring_revenue?: unknown;
  churn_rate?: number;
  payment_success_rate?: number;
  failed_payment_recovery_rate?: number;
}

interface PaymentAnalyticsPayload {
  payments?: {
    summary?: PaymentAnalyticsSummaryLike;
    by_provider?: Partial<Record<PaymentProvider, ProviderPaymentMetricsLike>>;
    subscription_trends?: TimeSeriesPaymentData[];
    trends?: TimeSeriesPaymentData[];
    revenue_by_tier?: Record<string, unknown>;
    generated_at?: string;
    period_days?: number;
  };
  generated_at?: string;
  period_days?: number;
}

const normalizeCurrencyAmount = (value: CurrencyAmountLike): CurrencyAmount | undefined => {
  if (!value || typeof value.amount !== 'number' || typeof value.currency !== 'string') {
    return undefined;
  }

  return {
    amount: value.amount,
    currency: value.currency,
  };
};

export class BillingPaymentApi extends BaseApiClient {
  constructor() {
    super('BillingPaymentAPI');
  }

  async getPaymentAnalytics(dateRange?: DateRange): Promise<ApiResponse<PaymentAnalytics>> {
    const params = dateRange ? { ...dateRange } : {};
    const response = await this.api.get<ApiResponse<PaymentAnalyticsPayload>>('/analytics/overview', { params });
    const payload = response.data?.data ?? response.data ?? {};
    const payments = payload.payments ?? {};

    const toCurrencyArray = (value: unknown): CurrencyAmount[] => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value.filter(
          (entry): entry is CurrencyAmount =>
            typeof entry === 'object' &&
            entry !== null &&
            typeof (entry as CurrencyAmount).amount === 'number' &&
            typeof (entry as CurrencyAmount).currency === 'string'
        ) as CurrencyAmount[];
      }
      if (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as CurrencyAmount).amount === 'number' &&
        typeof (value as CurrencyAmount).currency === 'string'
      ) {
        return [value as CurrencyAmount];
      }
      return [];
    };

    const summarySource = payments.summary ?? {};
    const summary: PaymentAnalyticsSummary = {
      total_revenue: toCurrencyArray(summarySource.total_revenue),
      monthly_recurring_revenue: toCurrencyArray(summarySource.monthly_recurring_revenue),
      annual_recurring_revenue: toCurrencyArray(summarySource.annual_recurring_revenue),
      churn_rate: typeof summarySource.churn_rate === 'number' ? summarySource.churn_rate : 0,
      payment_success_rate:
        typeof summarySource.payment_success_rate === 'number' ? summarySource.payment_success_rate : 0,
      failed_payment_recovery_rate:
        typeof summarySource.failed_payment_recovery_rate === 'number'
          ? summarySource.failed_payment_recovery_rate
          : undefined,
    };

    const byProviderSource = payments.by_provider ?? {};
    const byProvider = {} as Record<PaymentProvider, ProviderPaymentMetrics>;

    (['stripe', 'payple'] as PaymentProvider[]).forEach((provider) => {
      const metrics = byProviderSource[provider];
      if (!metrics) {
        return;
      }

      byProvider[provider] = {
        provider,
        active_subscriptions: metrics.active_subscriptions ?? 0,
        monthly_recurring_revenue:
          normalizeCurrencyAmount(metrics.monthly_recurring_revenue) ?? { amount: 0, currency: 'USD' },
        annual_recurring_revenue: normalizeCurrencyAmount(metrics.annual_recurring_revenue),
        churn_rate: typeof metrics.churn_rate === 'number' ? metrics.churn_rate : undefined,
        payment_success_rate:
          typeof metrics.payment_success_rate === 'number' ? metrics.payment_success_rate : undefined,
        failed_payments: typeof metrics.failed_payments === 'number' ? metrics.failed_payments : undefined,
      };
    });

    const subscriptionTrends: TimeSeriesPaymentData[] = Array.isArray(payments.subscription_trends)
      ? payments.subscription_trends
      : Array.isArray(payments.trends)
        ? payments.trends
        : [];

    const revenueByTier: Record<string, CurrencyAmount[]> | undefined = payments.revenue_by_tier
      ? Object.fromEntries(
        Object.entries(payments.revenue_by_tier).map(([tier, amounts]) => [tier, toCurrencyArray(amounts)])
      )
      : undefined;

    const paymentAnalytics: PaymentAnalytics = {
      summary,
      by_provider: byProvider,
      subscription_trends: subscriptionTrends,
      revenue_by_tier: revenueByTier,
      generated_at: payments.generated_at ?? payload.generated_at ?? new Date().toISOString(),
      period_days: payments.period_days ?? payload.period_days,
    };

    return {
      data: paymentAnalytics,
      message: response.data?.message ?? 'Payment analytics retrieved successfully',
      status: response.data?.status ?? 'success',
      timestamp: response.data?.timestamp ?? new Date().toISOString(),
    };
  }

  async getBillingSnapshot(userId: string): Promise<ApiResponse<BillingSnapshot>> {
    const response = await this.api.get<ApiResponse<BillingSnapshot>>(`/billing/users/${userId}`);
    return response.data;
  }

  async syncBillingData(userId: string, payload?: BillingSyncPayload): Promise<ApiResponse<BillingSyncResponse>> {
    const body = payload && payload.providers && payload.providers.length > 0 ? payload : {};
    const response = await this.api.post<ApiResponse<BillingSyncResponse>>(`/billing/users/${userId}/sync`, body);
    return response.data;
  }

  async recalculateUserTier(userId: string): Promise<ApiResponse<TierRecalculationResponse>> {
    const response = await this.api.post<ApiResponse<TierRecalculationResponse>>(`/billing/users/${userId}/recalculate-tier`);
    return response.data;
  }

  async overrideUserTier(userId: string, override: TierOverridePayload): Promise<ApiResponse<TierOverrideResponse>> {
    const response = await this.api.post<ApiResponse<TierOverrideResponse>>(`/billing/users/${userId}/override-tier`, override);
    return response.data;
  }

  async relinkSubscription(
    userId: string,
    payload: { subscription_id: string; provider?: 'stripe' }
  ): Promise<ApiResponse<{
    success: boolean;
    subscription_id: string;
    subscription_status: string;
    old_subscription_id: string | null;
    message: string;
  }>> {
    const response = await this.api.post(`/billing/users/${userId}/relink-subscription`, payload);
    return response.data;
  }

  /**
   * Link an existing Stripe customer (e.g. one created manually in the Stripe
   * dashboard) to a user's record. Optionally stamps `mediahub_user_id` metadata
   * on the Stripe customer so the app's lookup self-heals on future checkouts.
   */
  async linkStripeCustomer(
    userId: string,
    payload: { stripe_customer_id: string; stamp_metadata?: boolean }
  ): Promise<ApiResponse<{
    success: boolean;
    stripe_customer_id: string;
    old_stripe_customer_id: string | null;
    metadata_stamped: boolean;
    customer_email: string | null;
    warnings: string[];
    message: string;
  }>> {
    const response = await this.api.post(`/billing/users/${userId}/link-stripe-customer`, payload);
    return response.data;
  }

  async getBillingDrift(): Promise<ApiResponse<BillingDriftEntry[]>> {
    const response = await this.api.get<ApiResponse<BillingDriftEntry[]>>('/billing/drift');
    return response.data;
  }

  /**
   * List subscriptions scheduled to cancel at period end (Stripe + Payple),
   * including "overdue" accounts whose paid period has already elapsed but are
   * still on a paid tier (i.e. not yet downgraded).
   */
  async getPendingCancellations(): Promise<PendingCancellationsResponse> {
    const response = await this.api.get('/billing/pending-cancellations');
    const body = response.data;
    // Support both a bare body and a { success, data } envelope defensively.
    const payload: Partial<PendingCancellationsResponse> =
      body && Array.isArray(body.users) ? body : (body?.data ?? body ?? {});

    return {
      users: Array.isArray(payload.users) ? payload.users : [],
      summary: payload.summary ?? { total: 0, scheduled: 0, overdue: 0, stuck: 0 },
      generated_at: payload.generated_at ?? '',
    };
  }

  async getProviderCustomer(provider: PaymentProvider, customerId: string): Promise<ApiResponse<ProviderCustomerProfile>> {
    const response = await this.api.get<ApiResponse<ProviderCustomerProfile>>(`/billing/customers/${provider}/${customerId}`);
    return response.data;
  }

  async issueRefund(request: RefundRequest): Promise<ApiResponse<RefundResponse>> {
    const response = await this.api.post<ApiResponse<RefundResponse>>('/billing/refunds', request);
    return response.data;
  }

  // Billing Credits API
  /**
   * Get comprehensive credit summary for a user
   * @param userId - User ID
   * @param currency - Optional currency filter (USD or KRW)
   */
  async getUserBillingCredits(
    userId: string,
    currency?: CreditCurrency
  ): Promise<ApiResponse<UserCreditsResponse>> {
    const params = currency ? { currency } : {};
    const response = await this.api.get<ApiResponse<UserCreditsResponse>>(
      `/billing-credits/${userId}`,
      { params }
    );
    return response.data;
  }

  /**
   * Issue a billing credit to a user
   * @param request - Credit issuance details
   */
  async issueBillingCredit(
    request: IssueCreditRequest
  ): Promise<ApiResponse<IssueCreditResponse>> {
    const response = await this.api.post<ApiResponse<IssueCreditResponse>>(
      '/billing-credits/issue',
      request
    );
    return response.data;
  }

  /**
   * Manually consume credits for a user
   * @param request - Credit consumption details
   */
  async consumeBillingCredit(
    request: ConsumeCreditRequest
  ): Promise<ApiResponse<ConsumeCreditResponse>> {
    const response = await this.api.post<ApiResponse<ConsumeCreditResponse>>(
      '/billing-credits/consume',
      request
    );
    return response.data;
  }

  /**
   * Refresh user's cached credit balance
   * @param userId - User ID
   */
  async refreshCreditBalance(
    userId: string
  ): Promise<ApiResponse<RefreshBalanceResponse>> {
    const response = await this.api.post<ApiResponse<RefreshBalanceResponse>>(
      `/billing-credits/${userId}/refresh-balance`
    );
    return response.data;
  }

  /**
   * Get total outstanding credit liability across all users
   */
  async getOutstandingCreditLiability(): Promise<ApiResponse<OutstandingLiability>> {
    const response = await this.api.get<ApiResponse<OutstandingLiability>>(
      '/billing-credits/outstanding-liability'
    );
    return response.data;
  }

  /**
   * Cancel subscription with credit issuance (for fixing double billing)
   * @param userId - User ID
   * @param reason - Reason for cancellation and credit issuance
   */
  async cancelSubscriptionWithCredit(
    userId: string,
    reason: string
  ): Promise<ApiResponse<CancelSubscriptionWithCreditResponse>> {
    const request: CancelSubscriptionWithCreditRequest = { reason };
    const response = await this.api.post<ApiResponse<CancelSubscriptionWithCreditResponse>>(
      `/billing-credits/${userId}/cancel-subscription-with-credit`,
      request
    );
    return response.data;
  }
}

